"use client";

import { RegisteredComponent } from "@builder.io/sdk-react";
import dynamic from "next/dynamic";

import { IMAGE_FILE_TYPES } from "@/libs/builder/consts";
import { withValidation } from "@/libs/builder/validators/validateBuilderComponentData";

export const pushBlockInputs = [
  {
    name: "title",
    type: "string",
    required: true,
    defaultValue: "Push block title",
  },
  {
    name: "subtitle",
    type: "string",
    defaultValue: "Subtitle",
  },
  {
    name: "description",
    type: "richText",
    defaultValue:
      "Descriptive content lorem ipsum dolor sit amet, consetetur sadipscing elitr, sed diam nonumy eirmod tempor invidunt ut labore et dolore magna aliquyam erat, sed diam voluptua. At vero eos et",
  },
  {
    name: "cta",
    friendlyName: "Call to action label",
    type: "string",
    defaultValue: "Click me",
  },
  {
    name: "href",
    type: "url",
    defaultValue: "/",
  },
  {
    name: "desktopImage",
    friendlyName: "Image desktop",
    type: "file",
    allowedFileTypes: IMAGE_FILE_TYPES,
    required: true,
    defaultValue:
      "https://cdn.builder.io/api/v1/image/assets%2F90293f7dc9704cb5935f357315020051%2F035813b7aeb845139d31942675bcbb18",
  },
  {
    name: "mobileImage",
    friendlyName: "Image mobile",
    type: "file",
    allowedFileTypes: IMAGE_FILE_TYPES,
    defaultValue:
      "https://cdn.builder.io/api/v1/image/assets%2F90293f7dc9704cb5935f357315020051%2F66a0f35259e74956bd32ad83cc4490f5",
  },
  {
    name: "alt",
    friendlyName: "Alt Text",
    type: "string",
    defaultValue: "Push block image",
  },
  {
    name: "orientation",
    type: "string",
    enum: [
      "left-side-text",
      "right-side-text",
      "centered",
      "left-side-text-wide",
      "right-side-text-wide",
    ],
    required: true,
    defaultValue: "left-side-text",
  },
  {
    name: "mobileOrientation",
    type: "string",
    enum: ["bottom-text", "top-text"],
    defaultValue: "bottom-text",
  },
  {
    name: "smallHeightOnMobile",
    type: "boolean",
    defaultValue: true,
  },
  {
    name: "containedCTA",
    type: "boolean",
    defaultValue: true,
  },
  {
    name: "trackingEvent",
    type: "string",
    defaultValue: "",
    enum: [
      "none",
      "follow",
      "search",
      "phone_click",
      "contact_click",
      "ploom_club_click",
      "cta_click",
      "consumer_care_click",
      "store_locator_click",
    ],
  },
  {
    name: "logo",
    type: "object",
    defaultValue: {
      srcDark: "",
      link: "/",
      srcLight: "",
      title: "",
      alt: "Logo",
      isLogoEnabled: false,
    },
    subFields: [
      {
        name: "isLogoEnabled",
        type: "boolean",
      },
      {
        name: "link",
        type: "url",
      },
      {
        name: "srcDark",
        type: "file",
        allowedFileTypes: IMAGE_FILE_TYPES,
      },
      {
        name: "srcLight",
        type: "file",
        allowedFileTypes: IMAGE_FILE_TYPES,
      },
      {
        name: "title",
        type: "string",
      },
      {
        name: "alt",
        type: "string",
      },
    ],
  },
  {
    name: "secondButton",
    type: "object",
    friendlyName: "Second Button",
    defaultValue: {
      cta: "Click me",
      href: "/",
      isSecondButtonEnabled: false,
    },
    subFields: [
      {
        name: "isSecondButtonEnabled",
        type: "boolean",
      },
      {
        name: "cta",
        friendlyName: "Call to action label",
        type: "string",
      },
      {
        name: "href",
        type: "url",
        defaultValue: "/",
      },
    ],
  },
  {
    name: "blockContentProperties",
    type: "object",
    friendlyName: "Block Content Properties",
    subFields: [
      {
        name: "isBackgroundOverlayEnabled",
        friendlyName: "Enable desktop background overlay",
        type: "boolean",
      },
      {
        name: "isBackgroundOverlayEnabledOnMobile",
        friendlyName: "Enable mobile background overlay",
        type: "boolean",
      },
      {
        name: "isFullHeightContent",
        friendlyName: "Display the content at full height",
        type: "boolean",
      },
      {
        name: "isFullWidthContent",
        friendlyName: "Display the content at full width",
        type: "boolean",
      },
      {
        name: "blockHeight",
        friendlyName: "Define the content block's height",
        type: "string",
        enum: ["normalHeight", "miniHeight"],
      },
    ],
  },
  {
    name: "theme",
    type: "string",
    enum: ["light", "dark", "inherit"],
    defaultValue: "dark",
  },
] as const satisfies Inputs;

export const PushBlockComponent: RegisteredComponent = {
  name: "PushBlock",
  component: dynamic(async () =>
    withValidation(
      pushBlockInputs,
      (await import("./PushBlock")).PushBlock,
      "PushBlock"
    )
  ),
  inputs: pushBlockInputs,
};
